<template>
  <div>
    <div class="w-100" style="text-align: center" v-if="noData">
      {{ $t(`texts.data.no-rounds-completed`) }}
    </div>
    <apexchart
      v-else-if="selectedSubtype"
      :height="height"
      type="line"
      :options="chartOptions"
      :series="series"
    ></apexchart>
    <div v-else style="text-align: center">You have to choose a subtype!</div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "TeamRankingChart",
  data: () => ({
    noData: false,
    series: [],
    chartOptions: {
      colors: [
        "#008FFB",
        "#00E396",
        "#FEB019",
        "#FF4560",
        "#775DD0",
        "#3F51B5",
        "#546E7A",
        "#D4526E",
        "#8D5B4C",
        "#F86624",
        "#D7263D",
        "#1B998B",
        "#2E294E",
        "#F46036",
        "#E2C044",
      ],
      chart: {
        animations: {
          enabled: false,
        },
        //pozadí pod lajnama
        dropShadow: {
          enabled: true,
          color: "#000",
          top: 18,
          left: 7,
          blur: 10,
          opacity: 0.5,
        },
        // + - domeček
        toolbar: {
          show: true,
          offsetX: 0,
          offsetY: 0,
          tools: {
            download: true,
            selection: true,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false,
            customIcons: [],
          },
        },
      },
      //Ukazuje to obdélníčky s hodnatama na osách
      dataLabels: {
        enabled: true,
      },
      //Nadpis grafu
      title: {
        text: "Team ranking",
        align: "left",
      },
      //pozadí grafu
      grid: {
        //barva čar u Ylonových hodnot
        borderColor: "#e7e7e7",
        row: {
          //barva řádků ob 1
          colors: ["#f3f3f3", "transparent"],
          opacity: 0.5,
        },
      },
      //velikost teček na lajnách
      markers: {
        size: 10,
      },
      xaxis: {
        categories: [],
        title: {
          text: "Round",
        },
      },
      //Y osa
      yaxis: {
        min: 0,
        tickAmount: 15,
        title: {
          text: "Money",
        },
        //odsune doleva čísla na ose Y
        labels: {
          offsetX: -3,
          formatter: function (val) {
            let floor = Math.floor(val);
            return floor.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
          },
        },
      },
      //zobrazuje tečky vpravo nahoře pro zobrazení 1 lajny
      legend: {
        position: "top",
        horizontalAlign: "right",
        floating: true,
        offsetY: -25,
        offsetX: -5,
      },
      stroke: {
        show: true,
        curve: "straight",
        width: 3,
        dashArray: 0,
      },
    },
  }),
  props: {
    selectedSubtype: String,
    height: Number,
  },
  computed: {
    ...mapGetters([
      "activeGame",
      "allGameCampaigns",
      "financialOverview",
      "myOwnedActivateGames",
    ]),
    turn() {
      return this.activeGame.turn;
    },
    isActivatedGame() {
      return this.myOwnedActivateGames.length;
    },
    generateAllNumbers() {
      let arr = [];
      for (let i = 1; i <= this.turn; i++) {
        arr.push(i);
      }
      return arr;
    },
  },
  watch: {
    selectedSubtype: function () {
      this.createDataSets();
    },
    financialOverview: function () {
      this.createDataSets();
    },
    allGameCampaigns: function () {
      this.createDataSets();
    },
  },
  created() {
    if (this.selectedSubtype) {
      this.createDataSets();
    }
  },
  methods: {
    createDataSets: function () {
      this.series = [];

      if (this.turn === 1 && this.isActivatedGame) {
        this.noData = true;
        return false;
      } else {
        this.noData = false;
      }

      let storeDataFinance = this.financialOverview;
      let storeDataCampaigns = this.allGameCampaigns;

      if (this.selectedSubtype !== "teamRankingByAverageCampaignCosts") {
        storeDataFinance.forEach((oneTeam) => {
          this.series.push({
            name: oneTeam.name,
            data: this.createChartData(oneTeam.results),
          });
        });
      } else {
        storeDataCampaigns.forEach((oneTeam) => {
          this.series.push({
            name: oneTeam.name,
            data: this.createChartCampaignsData(oneTeam.results),
          });
        });
      }
    },
    createChartCampaignsData: function (results) {
      let rounds;
      if (this.isActivatedGame) {
        let local = [...this.generateAllNumbers];
        local.pop();
        rounds = local;
      } else {
        rounds = this.generateAllNumbers;
      }
      let data = [];
      rounds.forEach((round) => {
        if (results.some((r) => r.turn === round)) {
          let filtered = results.filter((r) => r.turn === round);
          let filteredPrice = filtered.map((f) => f.price);
          let average = this.countAverage(filteredPrice);
          data.push(average);
        } else {
          data.push(0);
        }
      });
      return data;
    },
    createChartData: function (results) {
      let data = [];
      let dataSource;
      if (this.selectedSubtype === "teamRankingByBudget") {
        dataSource = results;
      } else {
        dataSource = results.filter((r) => r.turn !== 0);
      }
      dataSource.forEach((r) => {
        if (this.selectedSubtype === "teamRankingByBudget") {
          data.push(r.metric);
        } else if (this.selectedSubtype === "teamRankingByCosts") {
          data.push(r.spent);
        } else if (this.selectedSubtype === "teamRankingByEarnings") {
          data.push(r.earned);
        } else if (this.selectedSubtype === "earningsVsCosts") {
          let ratio;
          if (!r.spent || !r.earned) {
            //0:0 0:1000 1000:0
            ratio = 0;
          } else {
            ratio = r.earned / r.spent;
            ratio = Math.round((ratio + Number.EPSILON) * 100) / 100;
          }
          data.push(ratio);
        }
      });
      return data;
    },
    countAverage(arr) {
      let sum = 0;
      for (let i in arr) {
        sum += arr[i];
      }
      let numbersCnt = arr.length;
      let counted = sum / numbersCnt;
      return Math.round((counted + Number.EPSILON) * 100) / 100;
    },
  },
};
</script>
